import { useEffect, useMemo, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CONTENT_PUBLIC_URL } from '@dnyaneshwari/shared';
import type { ChapterPart, ChapterShloka, Ovi, OviTimestamp } from '@dnyaneshwari/shared';
import { updatePart } from '@/services/adminService';

const CONTENT_BASE = (import.meta.env.VITE_CONTENT_BASE_URL as string | undefined) ?? CONTENT_PUBLIC_URL;

function fmt(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** "m:ss" or "m:ss.t" or plain seconds -> ms; null if unreadable. */
function parseClock(v: string): number | null {
  const t = v.trim();
  const m = /^(\d+):(\d{1,2})(?:\.(\d{1,3}))?$/.exec(t);
  if (m) return (Number(m[1]) * 60 + Number(m[2])) * 1000 + Number((m[3] ?? '0').padEnd(3, '0'));
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n * 1000) : null;
}

/** One thing the reciter can be on: an ovi, or a Gita shloka before an ovi. */
interface Row {
  key: string; // "o85" | "s1"
  kind: 'ovi' | 'shloka';
  number: number;
  text: string;
}

function rowKey(kind: Row['kind'], number: number): string {
  return `${kind === 'ovi' ? 'o' : 's'}${number}`;
}

/**
 * Where each ovi and shloka starts inside one recording.
 *
 * Two ways in: play the video and press "Mark" as the reciter reaches each
 * item (tap-to-sync), or import a JSON list produced from the burned-in
 * subtitles. Either way the table is editable before saving, and the player
 * follows it from the next app open. Rows run in reading order: a shloka
 * sits before the ovi it introduces, as the video recites it.
 */
export default function PartSyncPanel({
  chapterId,
  part,
  onClose,
}: {
  chapterId: string;
  part: ChapterPart;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [starts, setStarts] = useState<Map<string, number>>(
    () => new Map((part.oviTimestamps ?? []).map((t) => [
      t.shlokaNumber ? rowKey('shloka', t.shlokaNumber) : rowKey('ovi', t.oviNumber),
      t.startMs,
    ]))
  );
  // Where the voice stops inside an item, when that is known. Marking by hand
  // gives starts only, so an item then runs up to the next one; an imported
  // file carries measured ends (scripts/sync/refine-ends.js), and keeping them
  // stops the reader's gold from crawling through the music after a verse.
  const [ends, setEnds] = useState<Map<string, number>>(
    () => new Map((part.oviTimestamps ?? []).map((t) => [
      t.shlokaNumber ? rowKey('shloka', t.shlokaNumber) : rowKey('ovi', t.oviNumber),
      t.endMs,
    ]))
  );
  const [now, setNow] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [msg, setMsg] = useState('');

  const from = part.oviFrom > 0 ? part.oviFrom : 1;
  const to = part.oviTo >= from ? part.oviTo : from;

  useEffect(() => {
    const id = chapterId.padStart(2, '0');
    Promise.all([
      fetch(`${CONTENT_BASE}/chapters/${id}.json`).then((r) => (r.ok ? r.json() : { ovis: [] })),
      fetch(`${CONTENT_BASE}/chapters/${id}.shlokas.json`).then((r) => (r.ok ? r.json() : { shlokas: [] })),
    ]).then(([ch, sh]: [{ ovis: Ovi[] }, { shlokas: ChapterShloka[] }]) => {
      const out: Row[] = [];
      for (const o of ch.ovis) {
        if (o.oviNumber < from || o.oviNumber > to) continue;
        for (const s of sh.shlokas.filter((x) => x.beforeOvi === o.oviNumber)) {
          out.push({
            key: rowKey('shloka', s.shlokaNumber),
            kind: 'shloka',
            number: s.shlokaNumber,
            text: `${s.speaker ? s.speaker + '\n' : ''}${s.textSanskrit}`,
          });
        }
        out.push({ key: rowKey('ovi', o.oviNumber), kind: 'ovi', number: o.oviNumber, text: o.textMarathi });
      }
      setRows(out);
    });
  }, [chapterId, from, to]);

  // The next row to mark is the first without a start time.
  const nextToMark = useMemo(() => rows.find((r) => !starts.has(r.key)) ?? null, [rows, starts]);

  const current = useMemo(() => {
    let cur: Row | null = null;
    for (const r of rows) {
      const ms = starts.get(r.key);
      if (ms !== undefined && ms <= now) cur = r;
    }
    return cur;
  }, [rows, starts, now]);

  function mark() {
    const el = videoRef.current;
    if (!el || !nextToMark) return;
    setStarts((m) => new Map(m).set(nextToMark.key, Math.round(el.currentTime * 1000)));
    // A fresh start makes any measured end for that item meaningless.
    setEnds((m) => { const n = new Map(m); n.delete(nextToMark.key); return n; });
  }

  function undo() {
    const marked = rows.filter((r) => starts.has(r.key));
    const last = marked[marked.length - 1];
    if (!last) return;
    const ms = starts.get(last.key) ?? 0;
    setStarts((m) => { const n = new Map(m); n.delete(last.key); return n; });
    setEnds((m) => { const n = new Map(m); n.delete(last.key); return n; });
    const el = videoRef.current;
    if (el) el.currentTime = Math.max(0, ms / 1000 - 3);
  }

  function setOne(key: string, v: string) {
    const ms = parseClock(v);
    setEnds((m) => { const n = new Map(m); n.delete(key); return n; });
    setStarts((m) => {
      const next = new Map(m);
      if (ms === null || v.trim() === '') next.delete(key); else next.set(key, ms);
      return next;
    });
  }

  async function importJson(file: File) {
    try {
      const list = JSON.parse(await file.text()) as OviTimestamp[];
      const next = new Map<string, number>();
      const nextEnds = new Map<string, number>();
      for (const t of list) {
        const key = t.shlokaNumber
          ? rowKey('shloka', t.shlokaNumber)
          : t.oviNumber >= from && t.oviNumber <= to
            ? rowKey('ovi', t.oviNumber)
            : null;
        if (!key) continue;
        next.set(key, t.startMs);
        if (t.endMs > t.startMs) nextEnds.set(key, t.endMs);
      }
      setStarts(next);
      setEnds(nextEnds);
      const ovis = [...next.keys()].filter((k) => k.startsWith('o')).length;
      if (ovis === 0) {
        setMsg(`${file.name} holds no ovis in ${from}–${to}: is it the file for this part?`);
        return;
      }
      // An import is a decision already made, so it is saved at once: the
      // extra Save press was easy to forget, and the row then never said
      // "Synced".
      await saveList(next, nextEnds);
      setMsg(`Imported and saved ${ovis} of ${to - from + 1} ovis and ${next.size - ovis} shlokas from ${file.name}.`);
    } catch {
      setMsg('That file is not a timestamps JSON.');
    }
  }

  const save = () => saveList(starts, ends);

  async function saveList(startsMap: Map<string, number>, endsMap: Map<string, number>) {
    const starts = startsMap;
    const timed = rows.filter((r) => starts.has(r.key));
    // Reading order and time order must agree.
    for (let i = 1; i < timed.length; i++) {
      if ((starts.get(timed[i].key) ?? 0) < (starts.get(timed[i - 1].key) ?? 0)) {
        setMsg(`${label(timed[i])} starts before ${label(timed[i - 1])}. Fix that first.`);
        return;
      }
    }
    const list: OviTimestamp[] = timed.map((r, i) => ({
      oviNumber: r.kind === 'ovi' ? r.number : 0,
      ...(r.kind === 'shloka' ? { shlokaNumber: r.number } : {}),
      startMs: starts.get(r.key)!,
      // A measured end when we have one, so the fill stops with the voice
      // rather than stretching across the music that follows. It can never
      // outrun the next item.
      endMs: Math.min(
        endsMap.get(r.key) ?? Number.POSITIVE_INFINITY,
        i + 1 < timed.length ? starts.get(timed[i + 1].key)! : part.durationMs
      ),
    }));
    setSaving(true);
    try {
      await updatePart(chapterId, part.id, { oviTimestamps: list });
      setSaved(true);
      setMsg(`Saved ${list.length} timestamps.`);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  }

  const label = (r: Row) => (r.kind === 'ovi' ? `ovi ${r.number}` : `shloka ${r.number}`);
  const ovisTimed = rows.filter((r) => r.kind === 'ovi' && starts.has(r.key)).length;
  const missing = to - from + 1 - ovisTimed;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div>
            <h3 className="font-semibold text-gray-900">Sync ovis · {part.titleMarathi}</h3>
            <p className="text-xs text-gray-500">
              Ovis {from}–{to} · {ovisTimed} timed{missing > 0 ? `, ${missing} to go` : ', complete'} · {starts.size - ovisTimed} shlokas timed
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100"><XMarkIcon className="w-5 h-5 text-gray-500" /></button>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-[1.2fr_1fr]">
          {/* Video + controls */}
          <div className="flex flex-col min-h-0 border-r border-gray-200">
            <video
              ref={videoRef}
              // Audio-only parts play through the same element; it shows just
              // the controls, which is all tap-to-sync needs.
              src={part.videoUrl || part.audioUrl}
              controls
              preload="metadata"
              className="w-full bg-black aspect-video"
              onTimeUpdate={(e) => setNow(e.currentTarget.currentTime * 1000)}
            />
            <div className="p-4 space-y-3 overflow-auto">
              <div className="flex items-center gap-2">
                <button
                  onClick={mark}
                  disabled={!nextToMark}
                  className="px-4 py-2.5 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
                >
                  {nextToMark ? `Mark start of ${label(nextToMark)}` : 'Everything marked'}
                </button>
                <button onClick={undo} disabled={starts.size === 0} className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50">
                  Undo last
                </button>
                <span className="ml-auto text-sm text-gray-600 tabular-nums">{fmt(now)} · now on {current ? label(current) : 'invocation'}</span>
              </div>
              <p className="text-xs text-gray-500">
                Play the recording. The moment the reciter begins an ovi or a Gita shloka, press Mark. Marks fall in reading order from ovi {from}; use Undo to step back three seconds and re-mark.
                Or import a JSON made from the burned-in subtitles.
              </p>
              <div className="flex items-center gap-2 text-xs">
                <label className="px-3 py-1.5 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  Import timestamps JSON
                  <input type="file" accept="application/json" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) importJson(f); e.currentTarget.value = ''; }} />
                </label>
                <button onClick={() => { if (confirm('Clear every mark on this part?')) setStarts(new Map()); }} className="px-3 py-1.5 text-red-600 hover:underline">Clear all</button>
                {msg && <span className="text-gray-600">{msg}</span>}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-auto">
            {rows.map((r) => {
              const ms = starts.get(r.key);
              const isCurrent = current?.key === r.key;
              const isNext = nextToMark?.key === r.key;
              const isShloka = r.kind === 'shloka';
              return (
                <div
                  key={r.key}
                  className={`flex items-start gap-3 px-4 py-2 border-b border-gray-100 ${isCurrent ? 'bg-amber-50' : isNext ? 'bg-blue-50/60' : isShloka ? 'bg-amber-50/40' : ''}`}
                >
                  <span className={`w-12 text-xs font-semibold mt-1 ${isShloka ? 'text-amber-700' : 'text-gray-500'}`}>
                    {isShloka ? `श्लो ${r.number}` : r.number}
                  </span>
                  <input
                    value={ms === undefined ? '' : fmt(ms) + '.' + String(Math.floor((ms % 1000) / 100))}
                    placeholder="m:ss"
                    onChange={(e) => setOne(r.key, e.target.value)}
                    onFocus={() => { const el = videoRef.current; if (el && ms !== undefined) el.currentTime = ms / 1000; }}
                    className="w-20 px-2 py-1 border border-gray-200 rounded-md text-xs tabular-nums outline-none focus:border-amber-400"
                  />
                  <p className={`flex-1 text-sm font-marathi leading-relaxed whitespace-pre-line ${isShloka ? 'text-amber-900' : 'text-gray-800'}`}>{r.text}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-3 px-5 py-3 border-t border-gray-200">
          <button onClick={save} disabled={saving || starts.size === 0} className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-50">
            {saving ? 'Saving…' : saved ? 'Saved' : 'Save timestamps'}
          </button>
          <span className="text-xs text-gray-500">The app follows these from its next open. Partial lists are fine: untimed items are estimated or skipped.</span>
        </div>
      </div>
    </div>
  );
}
